/**
 * Refresh fax delivery status from SRFax and dual-write the result to
 * Firestore + PG `submission_output`.
 *
 * @module submissionStatus/hooks/useRefreshFaxStatus
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useState } from "react";
import { FirestoreService } from "../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../api/firebase/references";
import { getFaxStatus } from "../../../api/postCall/getFaxStatus";
import { ToastMessages } from "../../../components/toasts/toastMessages";
import { SingleOrderDetailsTableName } from "../../../enums/singleOrderDetailsTableName";
import { useUpdateOrderDetails } from "../../../hooks/singleMedicalPaOrderDetails/useUpdateOrderDetails";
import {
  dualWriteFaxSubmissionOutput,
  FaxSubmissionOutput,
  readFaxSubmissionOutput,
} from "../../../utils/faxSubmissionOutputPersistence";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { getCorrectStatusBasedOnFaxSubmissionStatus } from "../../finalDocuments/utils/faxSubmissionHelper";

interface UseRefreshFaxStatusProps {
  orderId: string | undefined;
}

export interface FaxStatusState {
  data?: string;
  loading?: boolean;
  error?: string;
}

/** PG update payload for merging into `submission.submission_output`. */
interface SubmissionOutputUpdate {
  submission_output: FaxSubmissionOutput;
}

/**
 * Provides `refreshFaxStatus` to re-poll SRFax and persist the normalized status.
 *
 * @param props - Order id whose fax metadata is refreshed
 * @returns Refresh handler and loading/error state for the UI
 */
export const useRefreshFaxStatus = ({ orderId }: UseRefreshFaxStatusProps) => {
  const [faxStatusState, setFaxStatusState] = useState<FaxStatusState>({
    data: undefined,
    loading: false,
    error: undefined,
  });
  const orgId = getOrgIdForFetchExternalWorklist();

  const { runUpdate: updateSubmissionOutput } =
    useUpdateOrderDetails<SubmissionOutputUpdate>({
      orderId: orderId ?? "",
      orgId: orgId ?? "",
      tableName: SingleOrderDetailsTableName.SUBMISSION,
    });

  const refreshFaxStatus = useCallback(async () => {
    if (!orderId) {
      toast.error(ToastMessages.ORDER_ID_NOT_FOUND);
      setFaxStatusState((prev) => ({
        ...prev,
        error: "Order ID not found",
        loading: false,
      }));
      return;
    }

    try {
      setFaxStatusState((prev) => ({
        ...prev,
        loading: true,
      }));

      // Step 1: fax_details_id + form_id — PG-first, Firestore fallback
      const faxInfo = await readFaxSubmissionOutput(orderId, orgId);
      const faxDetailsId =
        faxInfo?.fax_details_id != null ? String(faxInfo.fax_details_id) : "";
      const formId = faxInfo?.form_id ?? "";

      if (!faxDetailsId || !formId) {
        toast.error(ToastMessages.FAX_ID_NOT_FOUND);
        setFaxStatusState((prev) => ({
          ...prev,
          error: "Fax ID not found",
          loading: false,
        }));
        return;
      }

      // Step 2: Call getFaxStatus API
      const faxStatusResponse = await getFaxStatus({
        fax_details_id: faxDetailsId,
        form_id: formId,
        org_id: orgId ?? "",
      });

      const legacyFirestoreStatus = getCorrectStatusBasedOnFaxSubmissionStatus(
        faxStatusResponse.fax_status?.sent_status ?? "",
      );

      // Step 3: Dual-write fax_status to submission_output (PG + Firestore),
      // and keep the legacy order-doc Firestore status fields in sync.
      await Promise.all([
        dualWriteFaxSubmissionOutput({
          orderId,
          orgId,
          patch: {
            fax_status: legacyFirestoreStatus,
            fax_status_updated_at: new Date().toISOString(),
            submission: legacyFirestoreStatus,
          },
          updateSubmission: updateSubmissionOutput,
        }),
        FirestoreService.updateDocument(
          FirestoreCollectionReference.medicalPaOrders(),
          orderId,
          {
            "status.fax_submission_status": legacyFirestoreStatus,
            "status.submission": legacyFirestoreStatus,
          },
        ),
      ]);

      setFaxStatusState((prev) => ({
        ...prev,
        data: faxStatusResponse.fax_status?.sent_status ?? "",
        loading: false,
        error: undefined,
      }));
      toast.success(ToastMessages.FAX_STATUS_REFRESHED_SUCCESSFULLY);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      setFaxStatusState((prev) => ({
        ...prev,
        error: errorMessage,
        loading: false,
      }));
      toast.error(ToastMessages.ERROR_GETTING_FAX_STATUS);
    } finally {
      setFaxStatusState((prev) => ({
        ...prev,
        loading: false,
      }));
    }
  }, [orderId, orgId, updateSubmissionOutput]);

  return {
    refreshFaxStatus,
    faxStatusState,
    setFaxStatusState,
  };
};
